import { RefObject, useCallback, useEffect, useRef, useState } from "react";

import { getSessionID } from "../../analytics/useSnowplow-helpers";
import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import { useMediaContext } from "../../contexts/MediaContext";
import { readCookie, usePrevious } from "../../helpers/commonUtils";
import { setMediaMuted, setMediaVolume } from "../../helpers/volume";
import { castingSupported } from "../../service/cast-service";
import { createTemporaryLoginToken } from "../../service/login-api";
import { usePlayerStore } from "../../store/usePlayerStore";
import {
  getCastLoadRequest,
  getCastMediaInfo,
  initializeCastApi,
  seekFor
} from "../useChromecast/chromecast-helpers";
import { useChromecastTracks } from "../useChromecast/useChromecastTracks";
import { useSubscribeStoreOnce } from "../useSubscribeStoreOnce/useSubscribeStoreOnce";

export const useChromecast = ({
  videoRef
}: {
  videoRef: RefObject<HTMLVideoElement | undefined>;
}): void => {
  const { getState, useStoreState } = usePlayerStore();
  const snowplowEvents = useStoreState.snowplowEvents();
  const isCasting = useStoreState.isCasting();
  const isLive = useStoreState.isLive();
  const isPaused = useStoreState.isPaused();
  const player = useStoreState.player();
  const programId = useStoreState.programId();
  const { loggedIn } = useStoreState.loginData();

  const setCurrentTime = useStoreState.setCurrentTime();
  const setDurationInSeconds = useStoreState.setDurationInSeconds();
  const setIsCasting = useStoreState.setIsCasting();
  const setIsEnded = useStoreState.setIsEnded();
  const setIsMuted = useStoreState.setIsMuted();
  const setIsPaused = useStoreState.setIsPaused();
  const setIsPlaybackStarted = useStoreState.setIsPlaybackStarted();
  const setPlayer = useStoreState.setPlayer();
  const setSeekableEnd = useStoreState.setSeekableEnd();
  const setVolume = useStoreState.setVolume();

  const [isConnected, setIsConnected] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadedMediaId, setLoadedMediaId] = useState<string>();
  const [remotePlayer, setRemotePlayer] =
    useState<cast.framework.RemotePlayer>();
  const [remotePlayerController, setRemotePlayerController] =
    useState<cast.framework.RemotePlayerController>();

  const { chromecast, language } = useEmbedParametersContext();
  const { dvrWindowInSeconds } = useMediaContext();

  const isDvr = !!dvrWindowInSeconds && dvrWindowInSeconds > 0;

  const previousIsConnected = usePrevious(isConnected);
  const previousLoadedMediaId = usePrevious(loadedMediaId);
  const previousProgramId = usePrevious(programId);

  useChromecastTracks({
    remotePlayer,
    remotePlayerController
  });

  const mediaSessionRef = useRef<chrome.cast.media.Media>(undefined);
  const volumeBeforeCasting = useRef<{ muted: boolean; volume: number }>(
    undefined
  );

  const { subscribe, unsubscribe } = useSubscribeStoreOnce();

  const onCastApiInitialized = useCallback(() => {
    const onCastStateChanged = ({ castState }) => {
      if (castState === cast.framework.CastState.CONNECTED) {
        setIsConnected(true);
      } else {
        setIsConnected(false);
      }
    };
    const castInstance = cast.framework.CastContext.getInstance();
    castInstance.addEventListener(
      cast.framework.CastContextEventType.CAST_STATE_CHANGED,
      onCastStateChanged
    );
    castInstance.setOptions({
      autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      language,
      receiverApplicationId: __CHROMECAST_RECEIVER_APPLICATION_ID
    });
  }, [language]);

  const loadCastSessionMedia = useCallback(
    async (startFromBeginning = false) => {
      // Simulcast requires embedId (for example "yle-tv1"), if using programId, the serviceSchedule from chromecast does not work
      const idToCast = getState().isSimulcast ? getState().embedId : programId;
      const castSession =
        cast.framework.CastContext.getInstance().getCurrentSession();
      if (!castSession) {
        throw new Error("Failed to load media: Cast session does not exists");
      }
      if (!idToCast) {
        throw new Error("Failed to load media: Id is missing");
      }

      const snowplowSessionID = await getSessionID();

      setIsPaused(false);
      const token = await createTemporaryLoginToken(loggedIn);

      const currentTime = startFromBeginning
        ? undefined
        : videoRef.current?.currentTime;

      const request = getCastLoadRequest({
        consentCookie: {
          userconsent: readCookie("userconsent"),
          yleconsent: readCookie("yleconsent")
        },
        currentTime,
        language,
        mediaInfo: getCastMediaInfo(idToCast, isLive),
        snowplowSessionID,
        token
      });
      await castSession.loadMedia(request);
      setLoadedMediaId(idToCast);
      mediaSessionRef.current = castSession.getMediaSession() ?? undefined;
    },
    [getState, isLive, language, loggedIn, programId, setIsPaused, videoRef]
  );

  const onCastConnected = useCallback(async () => {
    const currentState = getState();
    setIsCasting(true);
    volumeBeforeCasting.current = {
      muted: currentState.isMuted,
      volume: currentState.volume
    };
    currentState.player.pause(); // Pause the video player before loading chromecast media
    setIsPaused(false);

    try {
      const castSession =
        cast.framework.CastContext.getInstance().getCurrentSession();
      if (!castSession) {
        throw new Error("Failed to load media: Cast session does not exists");
      }

      const castFrameworkRemotePlayer = new cast.framework.RemotePlayer();
      const castFrameworkRemotePlayerController =
        new cast.framework.RemotePlayerController(castFrameworkRemotePlayer);

      setIsMuted(castFrameworkRemotePlayer.isMuted);
      setVolume(castFrameworkRemotePlayer.volumeLevel);

      const isSameMedia =
        programId &&
        programId === castFrameworkRemotePlayer?.mediaInfo?.contentId;
      if (
        castSession.getSessionState() ===
          cast.framework.SessionState.SESSION_RESUMED &&
        castFrameworkRemotePlayer.isMediaLoaded &&
        isSameMedia
      ) {
        setIsPaused(castFrameworkRemotePlayer.isPaused);
        setIsPlaybackStarted(true);
        setIsMuted(castFrameworkRemotePlayer.isMuted);
        setVolume(castFrameworkRemotePlayer.volumeLevel);
        setLoadedMediaId(programId);
        mediaSessionRef.current =
          cast.framework.CastContext.getInstance()
            .getCurrentSession()
            ?.getMediaSession() ?? undefined;
      } else {
        await loadCastSessionMedia();
      }
      setRemotePlayer(castFrameworkRemotePlayer);
      setRemotePlayerController(castFrameworkRemotePlayerController);
    } catch (err) {
      console.error("Failed to load cast session media", err);
    }
  }, [
    getState,
    loadCastSessionMedia,
    programId,
    setIsCasting,
    setIsMuted,
    setIsPaused,
    setIsPlaybackStarted,
    setVolume
  ]);

  const onCastDisconnected = useCallback(() => {
    setRemotePlayer(undefined);
    setRemotePlayerController(undefined);
    setLoadedMediaId(undefined);
    setIsCasting(false);

    if (player.type == "chromecast") {
      // Wait for player controller to be updated to video controller
      subscribe("player", subscribedPlayer => {
        if (getState().isCasting) {
          unsubscribe();
        } else if (subscribedPlayer.type === "video") {
          subscribedPlayer.seekTo(getState().currentTime);

          if (volumeBeforeCasting.current) {
            // Update volume and mute state to same as it was before casting
            subscribedPlayer.setVolume(
              volumeBeforeCasting.current.volume,
              false
            );
            subscribedPlayer.setMuted(volumeBeforeCasting.current.muted, false);
          }

          if (!isPaused) {
            // play the video if it has not paused
            subscribedPlayer.play();
          }

          unsubscribe();
        }
      });
    }
  }, [getState, isPaused, player, setIsCasting, subscribe, unsubscribe]);

  const payload = useCallback(
    (playerArgument: cast.framework.RemotePlayer) => ({
      id: programId,
      isAudio: false,
      isCasting: true,
      isDvr,
      isLive,
      muted: playerArgument.isMuted,
      seekableEnd: 0, // ToDo fix
      time: playerArgument.currentTime
    }),
    [isDvr, isLive, programId]
  );

  const onPlayerStateChanged = useCallback(
    ({ value }) => {
      const currentIsCasting = getState().isCasting;
      if (!currentIsCasting || !remotePlayer) return;

      if (value === "PLAYING") {
        setIsPaused(false);
      }
      if (value === "PAUSED") {
        setIsPaused(true);
      }
      if (value === "IDLE") {
        setIsPaused(true);

        if (
          mediaSessionRef.current?.idleReason ===
          chrome.cast.media.IdleReason.FINISHED
        ) {
          setIsEnded(true);
          snowplowEvents?.onFinish();
        }
      }
    },
    [snowplowEvents, getState, remotePlayer, setIsEnded, setIsPaused]
  );

  const setStorePlayerController = useCallback(() => {
    if (!remotePlayer || !remotePlayerController) return;
    const play = async (options?: { throwError?: boolean }) => {
      if (remotePlayer.isMediaLoaded) {
        remotePlayerController.playOrPause();
      } else {
        try {
          await loadCastSessionMedia();
        } catch (err) {
          if (options?.throwError) throw err;
          console.error("Failed to play on chromecast", err);
        }
      }
    };
    setPlayer({
      autoplay: play,
      pause: () => remotePlayerController.playOrPause(),
      play,
      seekFor: amount =>
        seekFor(amount, isLive, remotePlayer, remotePlayerController),
      seekTo: newPosition => {
        remotePlayer.currentTime = newPosition;
        remotePlayerController.seek();
      },
      setMuted: isMuted =>
        setMediaMuted(
          { player: remotePlayer, playerController: remotePlayerController },
          isMuted,
          false
        ),
      setPlaybackRate: () =>
        console.warn("Playback rate cannot be changed during casting"),
      setVolume: volume =>
        setMediaVolume(
          { player: remotePlayer, playerController: remotePlayerController },
          volume,
          false
        ),
      type: "chromecast"
    });
  }, [
    isLive,
    loadCastSessionMedia,
    remotePlayer,
    remotePlayerController,
    setPlayer
  ]);

  useEffect(() => {
    // Initialize chromecast api
    if (!isInitialized && chromecast && castingSupported()) {
      initializeCastApi(onCastApiInitialized);
      setIsInitialized(true);
    }
  }, [chromecast, language, isLive, onCastApiInitialized, isInitialized]);

  useEffect(() => {
    // Wait for cast to be connected or disconnected
    if (isConnected && !previousIsConnected) {
      onCastConnected();
    } else if (!isConnected && previousIsConnected) {
      onCastDisconnected();
    }
  }, [isConnected, onCastConnected, onCastDisconnected, previousIsConnected]);

  useEffect(() => {
    if (programId && programId !== previousProgramId && remotePlayer) {
      loadCastSessionMedia(true).catch(err => {
        console.error("Failed to change media on cast", err);
      });
    }
  }, [
    loadCastSessionMedia,
    previousProgramId,
    programId,
    remotePlayer,
    videoRef
  ]);

  useEffect(() => {
    if (
      remotePlayer &&
      loadedMediaId &&
      loadedMediaId !== previousLoadedMediaId
    ) {
      setStorePlayerController();
    }
  }, [
    loadedMediaId,
    previousLoadedMediaId,
    remotePlayer,
    setStorePlayerController
  ]);

  useEffect(() => {
    if (!remotePlayer || !isCasting) return;
    const volumeLevelChanged = ({
      value
    }: cast.framework.RemotePlayerChangedEvent<number>) => {
      setVolume(value);
    };

    const isMutedChanged = ({
      value
    }: cast.framework.RemotePlayerChangedEvent<boolean>) => {
      setIsMuted(value);
    };

    const isConnectedChanged = ({
      value
    }: cast.framework.RemotePlayerChangedEvent<boolean>) => {
      const newCurrentTime = remotePlayer.savedPlayerState?.currentTime;
      if (!value && videoRef.current && newCurrentTime !== undefined) {
        videoRef.current.currentTime = newCurrentTime;
      }
    };

    const seekableRangeChanged = ({
      value
    }: cast.framework.RemotePlayerChangedEvent<chrome.cast.media.LiveSeekableRange>) => {
      if (isLive && value?.end) {
        setSeekableEnd(Math.round(value.end));
      }
    };

    const mediaInfoChanged = ({
      value
    }: cast.framework.RemotePlayerChangedEvent<chrome.cast.media.MediaInfo>) => {
      // Duration might be -1 or null for live or when not available
      if (value?.duration && value.duration > 0) {
        setDurationInSeconds(value.duration);
      }
    };

    const currentTimeChanged = ({
      value
    }: cast.framework.RemotePlayerChangedEvent<number>) => {
      setCurrentTime(value);
      if (videoRef.current) {
        videoRef.current.currentTime = value;
      }
    };

    const events: {
      event: cast.framework.RemotePlayerEventType;
      handler: (event: cast.framework.RemotePlayerChangedEvent) => void;
    }[] = [
      {
        event: cast.framework.RemotePlayerEventType.VOLUME_LEVEL_CHANGED,
        handler: volumeLevelChanged
      },
      {
        event: cast.framework.RemotePlayerEventType.IS_MUTED_CHANGED,
        handler: isMutedChanged
      },
      {
        event: cast.framework.RemotePlayerEventType.PLAYER_STATE_CHANGED,
        handler: onPlayerStateChanged
      },
      {
        event: cast.framework.RemotePlayerEventType.IS_CONNECTED_CHANGED,
        handler: isConnectedChanged
      },
      {
        event: cast.framework.RemotePlayerEventType.CURRENT_TIME_CHANGED,
        handler: currentTimeChanged
      },
      {
        event: cast.framework.RemotePlayerEventType.LIVE_SEEKABLE_RANGE_CHANGED,
        handler: seekableRangeChanged
      },
      {
        event: cast.framework.RemotePlayerEventType.MEDIA_INFO_CHANGED,
        handler: mediaInfoChanged
      }
    ];

    events.forEach(({ event, handler }) => {
      remotePlayerController?.addEventListener(event, handler);
    });

    return () => {
      events.forEach(({ event, handler }) => {
        remotePlayerController?.removeEventListener(event, handler);
      });
    };
  }, [
    isCasting,
    isLive,
    onPlayerStateChanged,
    payload,
    useStoreState,
    remotePlayer,
    remotePlayerController,
    setCurrentTime,
    setIsMuted,
    setSeekableEnd,
    setVolume,
    videoRef,
    setDurationInSeconds
  ]);
};
